import { productCardHelper } from '@/components/common/product-card/helper'
import { MAX_QUANTITY_PRODUCT } from '@/lib/constants/inventory'
import { getValue, groupBy } from '@/lib/utils'
import {
  BreadCrumbItem,
  CoolClubSaleFormatted,
  ETags,
  GroupedVariants,
  Option,
  Product,
  ProductCollectionResponse,
  ProductFormatted,
  ProductResponseFormatted,
  ProductVariant,
  StupidProduct,
  VariantMapped,
} from '@/types/product'
import {
  ExtraAttribute,
  ExtraFeature,
  ExtraImage,
  ExtraTechnicalSpecificationItem,
  ExtraThumbDescription,
  ProductDetail,
  ProductExtra,
  ProductPromotion,
} from '@/types/product-detail'

const MIN_PRICE_FREE_SHIPPING = 200000
const getColorOptions = (
  product: Pick<Product, 'options_value' | 'variant_gallery'>
): Option[] => {
  const options =
    product.options_value.find((option) =>
      option?.options_id?.includes('color')
    )?.options || ([] as Option[])

  if (!product.variant_gallery) return options

  return options.filter(
    (option: Option) =>
      Array.isArray(option.image) &&
      option.image.some((img) => img.src || img.image)
  )
}
const reFormatProductItem = (
  product: Product,
  isPreview?: boolean
): ProductFormatted | null => {
  const colorOptions = getColorOptions(product)

  const isCoolClubSale = product?.coolclub_sale
  const coolClubSale: CoolClubSaleFormatted = {
    active: false,
    coolclub_price: 0,
    rankings: [],
  }

  if (
    isCoolClubSale &&
    isCoolClubSale?.regular_price < product?.regular_price
  ) {
    coolClubSale.active = true
    coolClubSale.coolclub_price = isCoolClubSale?.regular_price
    coolClubSale.rankings = isCoolClubSale?.rankings
  }

  const { regular_price, compare_price, sale_percent } =
    productCardHelper.getPrices(product)

  const mappedVariants = transformVariantMapped(
    product,
    product.variants,
    colorOptions,
    isPreview
  )

  const isBasicProduct = !product.variants || product.variants.length === 0
  const isOutOfStock =
    typeof product.in_stock === 'number' && product.in_stock <= 0
  if (
    Object.keys(mappedVariants).length === 0 &&
    !(isPreview && isBasicProduct) &&
    !isOutOfStock
  ) {
    return null
  }

  const firstColor = Object.keys(mappedVariants)[0]
  const firstVariant = mappedVariants[firstColor]?.[0] || null
  const isFreeShipping = product?.regular_price >= MIN_PRICE_FREE_SHIPPING
  const fallbackThumbnail =
    product.images?.[0]?.src || product.images?.[0]?.image || ''
  const fallbackThumbnailHover =
    product.images?.[1]?.src ||
    product.images?.[1]?.image ||
    fallbackThumbnail

  return {
    title: product.title,
    href: product.seo_alias.includes('product')
      ? product.seo_alias
      : `/product/${product.seo_alias}`,
    is_setdo: product.is_setdo,
    id: product.id,
    regular_price,
    compare_price,
    sale_percent,
    note: product.addon_note_collection,
    active: true,
    mapped_variants: mappedVariants,
    icon_thumbnail: product?.icon_thumbnail,
    default_thumbnail: firstVariant?.thumbnail || fallbackThumbnail,
    default_thumbnail_hover:
      firstVariant?.thumbnail_hover || fallbackThumbnailHover,
    tags: getTags(product?.tags, product),
    is_free_shipping: isFreeShipping,
    is_quick_add: !firstVariant?.size,
    coolclub_sale: coolClubSale,
    review: product.review,
    sizechart: product.sizechart,
    size_guide: product.size_guide,
    gender_type: product.gender_type,
    collections: product.collections as string[],
    display_name_open: product.display_name_open,
    coming_soon: product.coming_soon,
    in_stock: product.in_stock,
  }
}

const reFormatProductsByCollectionResponse = (
  data: ProductCollectionResponse
): ProductResponseFormatted => {
  const products = data?.products?.data
    ?.map((p) => reFormatProductItem(p))
    .filter((item): item is ProductFormatted => !!item)

  return {
    data: products,
    is_next_page: data?.products?.current_page < data?.products?.last_page,
    last_page: data?.products?.last_page,
    current_page: data?.products?.current_page,
    total: data?.products?.total,
    // htmlSeoContent: data?.htmlSeoContent,
    titleDisplay: data?.titleDisplay,
    seo_alias: data?.detail?.seo_alias,
    title: data?.detail?.seo_title,
    request: data?.request,
  }
}

const transformComboVariantMapped = (
  product: Product,
  variants: ProductVariant[]
) => {
  const availableVariants = variants.filter((variant) => {
    if (variant?.hide) return false
    if (
      product?.apply_allowance_inventory &&
      variant.quantity <= MAX_QUANTITY_PRODUCT
    ) {
      return false
    }
    return true
  })

  // Deduplicate by option1+option2 combination - keep variant with highest quantity
  const variantMap = new Map<
    string,
    {
      id: string
      option1: string
      option2: string
      isDisable: boolean
      regular_price: number
      title: string
      quantity: number
    }
  >()

  availableVariants.forEach((variant) => {
    const option1 = variant.option1 || ''
    const option2 = variant.option2 || ''
    const key = `${option1}|${option2}` // Use pipe as separator for unique key

    const mappedVariant = {
      id: variant.id,
      option1: option1,
      option2: option2,
      isDisable: !variant.available,
      regular_price: variant.regular_price,
      title: variant.title,
      quantity: variant.quantity,
    }

    const existing = variantMap.get(key)
    if (existing) {
      console.error(
        `❌ [DUPLICATE COMBO VARIANT] Product "${product.title}" (ID: ${product.id}) has duplicate option1="${option1}" + option2="${option2}"`,
        '\n  - Existing variant:',
        { id: existing.id, quantity: existing.quantity },
        '\n  - Duplicate variant:',
        { id: variant.id, quantity: variant.quantity },
        '\n  - Keeping:',
        variant.quantity > existing.quantity
          ? 'New (higher quantity)'
          : 'Existing'
      )
    }
    // Keep the one with higher quantity to prioritize available stock
    // If quantities are equal, keep the first one
    if (!existing || variant.quantity > existing.quantity) {
      variantMap.set(key, mappedVariant)
    }
  })

  return Array.from(variantMap.values())
}

const transformVariantNoColorMapped = (
  product: Product,
  variants: ProductVariant[] = [],
  isPreview?: boolean
): GroupedVariants => {
  const availableVariants = variants.filter((variant) => {
    if (isPreview) return true
    if (variant?.hide) return false
    if (
      product?.apply_allowance_inventory &&
      variant.quantity <= MAX_QUANTITY_PRODUCT
    ) {
      return false
    }
    return true
  })

  if (availableVariants.length === 0) return {}

  // const groupVariant = groupBy(availableVariants, (item) => item.option1)

  // const updatedGroupVariant: GroupedVariants = Object.fromEntries(
  //   Object.entries(groupVariant).map(([key, items]) => [
  //     key,
  //     (items ?? []).map((variant) => ({
  //       id: variant.id,
  //       product_id: variant.product_id,
  //       size: variant.option2 || '',
  //       color: variant.option1 || '',
  //       quantity: variant.quantity,
  //       thumbnail: getValue(product, 'images.0.src', ''),
  //       thumbnail_hover: getValue(product, 'images.1.src', ''),
  //       colorImage: getValue(product, 'images.1.src', ''),
  //       isDisable: !variant.available,
  //       regular_price: variant.regular_price,
  //       title: variant.title,
  //     })),
  //   ])
  // )

  // Deduplicate by size - keep variant with highest quantity when duplicates exist
  const sizeMap = new Map<string, VariantMapped>()

  availableVariants.forEach((variant) => {
    const size = variant.option1 || ''
    const mappedVariant: VariantMapped = {
      id: variant.id,
      product_id: variant.product_id,
      size: size,
      color: '',
      quantity: variant.quantity,
      thumbnail: getValue(product, 'images.0.src', ''),
      thumbnail_hover: getValue(product, 'images.1.src', ''),
      colorImage: getValue(product, 'images.1.src', ''),
      isDisable: !variant.available,
      regular_price: variant.regular_price,
      title: variant.title,
    }

    const existing = sizeMap.get(size)
    if (existing) {
      // console.error(
      //   `❌ [DUPLICATE SIZE] Product "${product.title}" (ID: ${product.id}) has duplicate size "${size}"`,
      //   '\n  - Existing variant:',
      //   { id: existing.id, quantity: existing.quantity },
      //   '\n  - Duplicate variant:',
      //   { id: variant.id, quantity: variant.quantity },
      //   '\n  - Keeping:',
      //   variant.quantity > existing.quantity
      //     ? 'New (higher quantity)'
      //     : 'Existing'
      // )
    }
    // Keep the one with higher quantity to prioritize available stock
    // If quantities are equal, keep the first one
    if (!existing || variant.quantity > existing.quantity) {
      sizeMap.set(size, mappedVariant)
    }
  })

  const updatedGroupVariant = {
    // Nếu không có màu thì map thành FAKE, Cần chú ý khi lấy schema sản phẩm không truyền color FAKE vào
    FAKE: Array.from(sizeMap.values()),
  }

  return updatedGroupVariant
}

export const transformVariantMapped = (
  product: Product,
  variants: ProductVariant[] = [],
  colorOptions: Option[],
  isPreview?: boolean,
  bypassStockFilter?: boolean
): GroupedVariants => {
  if (colorOptions?.length === 0) {
    // nếu không có màu nhưng có variants thì map từ variants
    if (variants?.length > 0) {
      return transformVariantNoColorMapped(product, variants, isPreview)
    }

    return {}
  }

  const colorOptionsHashMap = new Map(
    colorOptions?.map((item) => [item.title, item])
  )

  const availableVariant = variants?.filter((variant) => {
    if (isPreview) {
      return true
    }
    // nếu tích ẩn biến thể mặc dù tồn còn nhiều thì size cũng phải ẩn , nếu tích ẩn size thì màu đấy cũng ẩn luôn
    if (variant?.hide) return false

    //nếu tích "Áp dụng ngưỡng lưu kho tối thiểu (Chỉ bán nếu tồn kho lớn hơn 5)" đến hết thì phải hiển thị
    // if (
    //   product?.apply_allowance_inventory &&
    //   variant.quantity <= MAX_QUANTITY_PRODUCT
    // ) {
    //   return false
    // }
    return true
  })

  if (availableVariant.length === 0) return {}

  const groupVariant = groupBy(availableVariant, (item) => item.option1)

  // Check for color option mismatch (language inconsistency)
  const hasMismatch = Object.keys(groupVariant).some(
    (key) => !colorOptionsHashMap.has(key)
  )

  if (hasMismatch) {
    console.error('❌ [CRITICAL] Color options language mismatch detected!')
    console.error(
      '❌ Backend is returning different languages for color options vs variants'
    )
    console.error(
      '❌ Color Options (from options_value):',
      Array.from(colorOptionsHashMap.keys())
    )
    console.error(
      '❌ Variant Colors (from variants.option1):',
      Object.keys(groupVariant)
    )
    console.error('❌ This needs to be fixed in Backend API!')
    console.error(
      '❌ Using FALLBACK logic to prevent products from being hidden...'
    )
  }

  const updatedGroupVariant: GroupedVariants = Object.fromEntries(
    Object.entries(groupVariant)
      // If mismatch detected, don't filter out - use fallback
      .filter(([key]) => {
        if (hasMismatch) {
          // During mismatch, keep all variants
          return true
        }
        // Normal flow: only keep if exists in colorOptionsHashMap
        return colorOptionsHashMap.has(key)
      })
      .map(([key, items]) => {
        // Deduplicate by size - keep variant with highest quantity when duplicates exist
        const sizeMap = new Map<string, VariantMapped>()

        ;(items ?? []).forEach((item) => {
          const valueColor = colorOptionsHashMap.get(item?.option1)

          // Log warning if using fallback for this specific item
          if (!valueColor && hasMismatch) {
            console.warn(
              `⚠️ [FALLBACK] Using product images for variant color "${item?.option1}"`
            )
          }

          const colorImages = valueColor?.image || []
          const validImages = colorImages.filter((img) => img.src || img.image)
          const finalThumbnail =
            validImages[0]?.src || validImages[0]?.image || ''
          const finalThumbnailHover =
            validImages[1]?.src || validImages[1]?.image || finalThumbnail
          const colorImage =
            validImages[validImages.length - 1]?.src ||
            validImages[validImages.length - 1]?.image ||
            finalThumbnail

          const size = item?.option2 || ''
          const mappedVariant: VariantMapped = {
            id: item.id,
            size: size,
            color: item?.option1,
            colorImage: colorImage,
            quantity: item.quantity,
            isDisable:
              item.quantity <= 0 ||
              (product?.apply_allowance_inventory &&
                item.quantity <= MAX_QUANTITY_PRODUCT),
            product_id: item.product_id,
            thumbnail: finalThumbnail,
            thumbnail_hover: finalThumbnailHover,
            regular_price: item.regular_price,
            title: item.title,
          }

          const existing = sizeMap.get(size)
          if (existing) {
            // console.error(
            //   `❌ [DUPLICATE SIZE] Product "${product.title}" (ID: ${product.id}) has duplicate size "${size}" in color "${key}"`,
            //   '\n  - Existing variant:',
            //   { id: existing.id, quantity: existing.quantity },
            //   '\n  - Duplicate variant:',
            //   { id: item.id, quantity: item.quantity },
            //   '\n  - Keeping:',
            //   item.quantity > existing.quantity
            //     ? 'New (higher quantity)'
            //     : 'Existing'
            // )
          }
          // Keep the one with higher quantity to prioritize available stock
          // If quantities are equal, keep the first one
          if (!existing || item.quantity > existing.quantity) {
            sizeMap.set(size, mappedVariant)
          }
        })

        return [key, Array.from(sizeMap.values())]
      })
      .filter((entry) => {
        const value = entry[1] as VariantMapped[]
        if (isPreview || bypassStockFilter) return true
        // Hide if color has no thumbnail (only for variant_gallery products)
        if (product.variant_gallery && !value[0]?.thumbnail) return false
        // Hide if only 1 variant and it's disabled
        if (value.length === 1 && value[0].isDisable) {
          return false
        }
        // Hide if all variants are disabled
        if (value.every((variant) => variant.isDisable)) {
          return false
        }
        return true
      })
  )

  // If there was a mismatch, return variants directly without ordering
  // because colorOptionsHashMap keys won't match updatedGroupVariant keys
  if (hasMismatch) {
    console.warn(
      '⚠️ [FALLBACK] Skipping color ordering due to language mismatch'
    )
    return updatedGroupVariant
  }

  // Normal flow: order by colorOptionsHashMap
  const orderedImageObject: GroupedVariants = {}

  for (const [key] of colorOptionsHashMap) {
    if (updatedGroupVariant[key]) {
      orderedImageObject[key] = updatedGroupVariant[key]
    }
  }

  return orderedImageObject
}

const getTags = (tags: Record<string, string>, product: Product) => {
  if (product.preorder && product.addon_note) {
    return [ETags.PREORDER]
  }
  const validTags = Object.values(tags).filter((tag) => tag !== 'cleandye')

  if (validTags.length > 0) {
    const firstTag = validTags[0]

    if (product._id === '650428bfec1126721240dcb4') {
      return [ETags.LIMITED_QUANTITY]
    }

    return [firstTag]
  }

  return validTags
}

// Helper function to validate ExtraAttribute
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraAttribute = (attr: any): attr is ExtraAttribute => {
  return (
    typeof attr === 'object' &&
    attr !== null &&
    ((typeof attr.key === 'string' && attr.key.trim() !== '') ||
      (typeof attr.id === 'string' && attr.id.trim() !== '')) &&
    typeof attr.title === 'string' &&
    attr.title.trim() !== '' &&
    typeof attr.src_icon === 'string' &&
    attr.src_icon.trim() !== ''
  )
}

// Helper function to validate ExtraImage
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraImage = (img: any): img is ExtraImage => {
  return (
    typeof img === 'object' &&
    img !== null &&
    typeof img.src_img === 'string' &&
    img.src_img.trim() !== ''
  )
}

// Helper function to validate ExtraFeature
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidExtraFeature = (feature: any): feature is ExtraFeature => {
  return (
    typeof feature === 'object' &&
    feature !== null &&
    typeof feature.title === 'string' &&
    feature.title.trim() !== '' &&
    Array.isArray(feature.value) &&
    feature.value.length > 0 &&
    // Kiểm tra xem mỗi item trong value có title hợp lệ không
    feature.value.some(
      (item: { title?: string }) =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.title === 'string' &&
        item.title.trim() !== ''
    )
  )
}

// Helper function to validate ExtraThumbDescription
const isValidExtraThumbDescription = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  thumb: any
): thumb is ExtraThumbDescription => {
  return (
    typeof thumb === 'object' &&
    thumb !== null &&
    typeof thumb.src_img === 'string' &&
    thumb.src_img.trim() !== ''
  )
}

const validateBreadCrumb = (bread_crumb: unknown): BreadCrumbItem[] => {
  if (Array.isArray(bread_crumb)) {
    return bread_crumb
  }

  if (
    typeof bread_crumb === 'object' &&
    bread_crumb !== null &&
    !Array.isArray(bread_crumb)
  ) {
    return Object.values(bread_crumb)
  }

  return []
}

// Validate and normalize ProductExtra
const validateProductExtra = (extra?: ProductExtra): ProductExtra => {
  if (!extra || typeof extra !== 'object') {
    return {
      p_extra_attributes: [],
      p_extra_images: [],
      p_extra_features: [],
      p_extra_banner_section_tech: [],
      p_extra_short_description: '',
      p_extra_thumb_description: { src_img: '', src_alt: '' },
      p_extra_description: { src_desktop: '', src_mobile: '', src_alt: '' },
      p_extra_support_movement: '',
      p_extra_technical_specifications: [] as ExtraTechnicalSpecificationItem[],
    }
  }

  return {
    // Mảng
    p_extra_attributes: Array.isArray(extra.p_extra_attributes)
      ? extra.p_extra_attributes.filter(isValidExtraAttribute)
      : [],

    p_extra_images: Array.isArray(extra.p_extra_images)
      ? extra.p_extra_images.filter(isValidExtraImage)
      : [],

    p_extra_features: Array.isArray(extra.p_extra_features)
      ? extra.p_extra_features.filter(isValidExtraFeature)
      : [],

    p_extra_banner_section_tech: Array.isArray(
      extra.p_extra_banner_section_tech
    )
      ? extra.p_extra_banner_section_tech.filter(
          (item) => typeof item === 'object' && item !== null
        )
      : [],

    p_extra_technical_specifications: (() => {
      const specs = extra.p_extra_technical_specifications
      if (!specs) return [] as ExtraTechnicalSpecificationItem[]
      // Nếu là mảng trực tiếp (backward compatibility)
      if (Array.isArray(specs)) return specs
      // Nếu là object có isHide và items
      if (
        typeof specs === 'object' &&
        specs !== null &&
        !Array.isArray(specs) &&
        'isHide' in specs &&
        'items' in specs
      ) {
        const specObj = specs as { isHide: boolean; items: unknown[] }
        return specObj.isHide === false
          ? (specObj.items as ExtraTechnicalSpecificationItem[])
          : ([] as ExtraTechnicalSpecificationItem[])
      }
      return [] as ExtraTechnicalSpecificationItem[]
    })(),

    // Chuỗi
    p_extra_short_description:
      typeof extra.p_extra_short_description === 'string'
        ? extra.p_extra_short_description
        : '',

    p_extra_support_movement:
      typeof extra.p_extra_support_movement === 'string'
        ? extra.p_extra_support_movement
        : '',

    // Object
    p_extra_thumb_description: isValidExtraThumbDescription(
      extra.p_extra_thumb_description
    )
      ? extra.p_extra_thumb_description
      : { src_img: '', src_alt: '' },

    p_extra_description:
      typeof extra.p_extra_description === 'object' &&
      extra.p_extra_description !== null
        ? extra.p_extra_description
        : { src_desktop: '', src_mobile: '', src_alt: '' },
  }
}

// const getPricingPolicy = (product: ProductDetail) => {
//   const { pricing_policy, collection_pricing_policy } = product
//   return {
//     pricing_policy,
//     collection_pricing_policy,
//   }
// }

const transformCombo = (data: ProductDetail) => {
  if (!data.is_setdo) return null

  const colorOptions =
    data?.options_value.find((option) => {
      return option?.options_id?.includes('color')
    })?.options || ([] as Option[])
  const colorHashMap = new Map(colorOptions.map((item) => [item.title, item]))

  const options = data.options.map((item) => {
    return {
      title: item.title,
      values: item?.values.map((value) => ({
        label: value,
        value,
        image: colorHashMap.get(value)?.image[0]?.src || '',
      })),
      option_id: item?.option_id,
    }
  })

  return {
    options,
    variants: transformComboVariantMapped(data, data.variants),
  }
}

const reFormatProductDetailResponse = (
  data: ProductDetail,
  isPreview?: boolean
) => {
  const colorOptions = getColorOptions(data)

  const { regular_price, compare_price, sale_percent } =
    productCardHelper.getPrices(data)

  const isOutOfStock =
    typeof data.in_stock === 'number' && data.in_stock <= 0
  const bypassStockFilter = Boolean(data.coming_soon) || isOutOfStock

  const mappedVariants = transformVariantMapped(
    data,
    data.variants,
    colorOptions,
    isPreview,
    bypassStockFilter
  )

  const combo = transformCombo(data)

  // Filter out color options that don't exist in mappedVariants
  const usedColors = Object.keys(mappedVariants)
  const filteredColorOptions = colorOptions.filter((option) =>
    usedColors.includes(option.title)
  )

  // Update color options in the product
  if (data.options_value) {
    const colorOptionIndex = data.options_value.findIndex((option) =>
      option?.options_id?.includes('color')
    )
    if (colorOptionIndex !== -1) {
      data.options_value[colorOptionIndex].options = filteredColorOptions
    }
  }

  // Validate and normalize extra data
  const validatedExtra = validateProductExtra(data.extra)

  // Get default thumbnail from first variant
  const firstColor = Object.keys(mappedVariants)[0]
  const firstVariant = mappedVariants[firstColor]?.[0] || null
  const default_thumbnail = firstVariant?.thumbnail || ''

  let promotionsActive: ProductPromotion[] = []

  if (data?.promotions?.length > 0) {
    promotionsActive = data?.promotions?.filter((item) => item.show_in_detail)
  }

  // const policy = getPricingPolicy(data)

  const coolClubSale = data?.coolclub_sale
  const coolClubSaleFormatted: CoolClubSaleFormatted = {
    active: false,
    coolclub_price: 0,
    rankings: [],
  }

  if (coolClubSale && coolClubSale?.regular_price < data?.regular_price) {
    coolClubSaleFormatted.active = true
    coolClubSaleFormatted.coolclub_price = coolClubSale?.regular_price
    coolClubSaleFormatted.rankings = coolClubSale?.flash_sale?.rankings
  }

  return {
    ...data,
    bread_crumb: validateBreadCrumb(data?.bread_crumb),
    regular_price,
    compare_price,
    sale_percent,
    mapped_variants: mappedVariants,
    color_options: filteredColorOptions,
    extra: validatedExtra,
    default_thumbnail,
    promotions: promotionsActive,
    coolclub_sale: coolClubSaleFormatted,
    combo,
  }
}

// =============================== Search Spotlight ===============================
const reFormatProductsSpotlightResponse = (data: StupidProduct[]) => {
  try {
    const products = data
      ?.map((product) => {
        const colorOptions = getColorOptions(product)

        const isCoolClubSale = product?.coolclub_sale
        const coolClubSale: CoolClubSaleFormatted = {
          active: false,
          coolclub_price: 0,
          rankings: [],
        }

        if (
          isCoolClubSale &&
          isCoolClubSale?.regular_price < product?.regular_price
        ) {
          coolClubSale.active = true
          coolClubSale.coolclub_price = isCoolClubSale?.regular_price
          coolClubSale.rankings = isCoolClubSale?.rankings
        }

        const { regular_price, compare_price, sale_percent } =
          productCardHelper.getPrices(product)

        const mappedVariants = transformVariantMapped(
          product,
          product.variants,
          colorOptions
        )

        const firstColor = Object.keys(mappedVariants)[0]
        const firstVariant = mappedVariants[firstColor]?.[0] || null
        const isFreeShipping = product?.regular_price >= MIN_PRICE_FREE_SHIPPING

        return {
          title: product.title,
          href: product.seo_alias.includes('product')
            ? product.seo_alias
            : `/product/${product.seo_alias}`,
          is_setdo: product.is_setdo,
          id: product.id,
          regular_price,
          compare_price,
          sale_percent,
          note: product.addon_note,
          active: Boolean(Object.keys(mappedVariants).length),
          mapped_variants: mappedVariants,
          icon_thumbnail: product?.icon_thumbnail,
          default_thumbnail: firstVariant?.thumbnail,
          default_thumbnail_hover: firstVariant?.thumbnail_hover,
          tags: getTags(product?.tags, product),
          is_free_shipping: isFreeShipping,
          is_quick_add: !firstVariant?.size,
          coolclub_sale: coolClubSale,
          // TODO: bắt be thêm key isX2CoolCash vào product, hiện tại con cũ lấy từ api 1 list sản phẩm ra rùi includes rất là đần
        }
      })
      .filter((item) => item?.active)

    return products
  } catch (error) {
    console.error('🧙 ~ error:dcmmmmmmmmmmmm', error)
    return []
  }
}

const reFormatProductResponse = (data: Product[]) => {
  if (!data || !Array.isArray(data)) return []

  return data.map((product) => {
    const colorOptions = getColorOptions(product)

    const { regular_price, compare_price, sale_percent } =
      productCardHelper.getPrices(product)

    const mappedVariants = transformVariantMapped(
      product,
      product.variants,
      colorOptions
    )

    const firstColor = Object.keys(mappedVariants)[0]
    const firstVariant = mappedVariants[firstColor]?.[0] || null

    return {
      id: product.id,
      display_name: product.title || product.display_name,
      display_name_open: product.display_name_open || '',
      seo_alias: product.seo_alias,
      regular_price,
      compare_price,
      percent: sale_percent,
      mapped_variants: mappedVariants,
      default_thumbnail: firstVariant?.thumbnail || product?.images[0].src,
      default_thumbnail_hover:
        firstVariant?.thumbnail_hover || product?.images[1].src,
      title: product.display_name,
      href: `/product/${product.seo_alias}`,
      is_setdo: product?.is_setdo,
      note: product.addon_note,
      active: Boolean(Object.keys(mappedVariants).length),
      tags: getTags(product?.tags, product),
      is_free_shipping: product.regular_price >= MIN_PRICE_FREE_SHIPPING,
      is_quick_add: !firstVariant?.size,
      sale_percent: sale_percent,
      images: product.images,
      options_value: product.options_value,
      tag_text: product?.tag_text,
      size_guide: product?.size_guide,
      gender_type: product?.gender_type,
      sizechart: product?.sizechart,
      tag_label: product?.tag_label,
      sold_product: product?.sold_product,
    }
  })
}

const reFormatProductPromotionResponse = (data: ProductPromotion[]) => {
  if (!data || !Array.isArray(data)) return []

  return data.map((product) => {
    const colorOptions =
      product.options.find((option) => option?.option_id?.includes('color'))
        ?.values || []

    const sizeOptions =
      product.options.find((option) => option?.option_id?.includes('size'))
        ?.values || []

    const thumbnail = product?.images?.[0]?.src || ''

    const formatColorOptions = colorOptions.map((color) => ({
      label: color,
      value: color,
    }))

    const formatSizeOptions = sizeOptions.map((size) => ({
      label: size,
      value: size,
    }))

    return {
      ...product,
      colorOptions: formatColorOptions,
      sizeOptions: formatSizeOptions,
      thumbnail,
    }
  })
}

const reFormatProductSearchResponse = (data: Product[]) => {
  // thay images bằng biến thể đầu tiên từ variants
  return data.map((product) => {
    const colorOptions = getColorOptions(product)

    const mappedVariants = transformVariantMapped(
      product,
      product.variants,
      colorOptions
    )

    const firstColor = Object.keys(mappedVariants)[0]
    const firstVariant = mappedVariants[firstColor]?.[0] || null
    const default_thumbnail = firstVariant?.thumbnail || ''

    return {
      ...product,
      images: [
        {
          id: product.id,
          src: default_thumbnail,
          image: default_thumbnail,
        },
      ],
    }
  })
}

const reFormatProductList = (
  products: Product[],
  isPreview?: boolean
): ProductFormatted[] => {
  if (!products || !Array.isArray(products)) return []
  return products
    .map((p) => reFormatProductItem(p, isPreview))
    .filter((item): item is ProductFormatted => !!item)
}

export const ProductFormatter = {
  reFormatProductsByCollectionResponse,
  reFormatProductDetailResponse,
  reFormatProductsSpotlightResponse,
  reFormatProductResponse,
  reFormatProductPromotionResponse,
  reFormatProductSearchResponse,
  reFormatProductList,
}
